import {useSwitchCustomer} from '@/composables/useSwitchCustomer';
import {useSettingsTabs} from '@/composables/useSettingsTabs';
import {useLocalisation} from '@/composables/useLocalisation';
import {settingsKey, type SettingsKey} from '@/components/settings/injectionKeys';
import {logoutKey} from '@/injectionKeys';

/**
 * A command offered by the spotlight search. `switch_customer` commands are returned by the search
 * API (api/cms/pages/?search=, see apps/search/commands.py); the others are local.
 * To add an action: add it to the union here & a handler, icon and heading to the registry below.
 */
export type SearchCommand =
  | {
      action: 'switch_customer';
      label: string;
      icon?: string;
      payload: {customer_id: number; debtor_number: string};
    }
  | {action: 'open_settings'; label: string; icon?: string; payload: {tab: SettingsKey}}
  | {
      action: 'switch_theme';
      label: string;
      icon?: string;
      payload: {theme: 'light-only' | 'dark-only'};
    }
  | {
      action: 'switch_locale';
      label: string;
      icon?: string;
      payload: {language: 'en-US' | 'nl-NL'};
    }
  | {action: 'logout'; label: string; icon?: string};

type Action = SearchCommand['action'];

/**
 * Maps search command actions to the function that executes them.
 */
export function useSearchCommands() {
  const {translate} = useLocalisation();
  const {switchCustomer} = useSwitchCustomer();
  const settingsTab = inject(settingsKey, ref<SettingsKey>(''));
  const {settingsList} = useSettingsTabs();
  const userSettingsStore = useUserSettingsStore();
  const coresStore = useCoresStore();
  const logout = inject(logoutKey);

  const handlers: {
    [A in Action]: (command: Extract<SearchCommand, {action: A}>) => void | Promise<void>;
  } = {
    switch_customer: (command) => switchCustomer(command.payload.customer_id),
    open_settings: (command) => {
      settingsTab.value = command.payload.tab;
    },
    // Applied locally first, so the change is instant: persisting is what hits the API.
    switch_theme: async (command) => {
      userSettingsStore.setTheme(command.payload.theme);
      await userSettingsStore.save();
    },
    switch_locale: async (command) => {
      userSettingsStore.setLanguage(command.payload.language);
      await userSettingsStore.save();
      await coresStore.switchLanguage(); // Navigation & pages are per language
    },
    // Strictly no arguments: a truthy one reports a failed sign-in, see App.vue
    logout: () => logout?.(),
  };

  const icons: Record<Action, string> = {
    switch_customer: 'swap-horiz',
    open_settings: 'settings-outline',
    switch_theme: 'contrast',
    switch_locale: 'translate',
    logout: 'logout',
  };

  const headings: Record<Action, string> = {
    switch_customer: 'Switch customer',
    open_settings: 'Settings',
    switch_theme: 'Preferences',
    switch_locale: 'Preferences',
    logout: 'Settings',
  };

  const execute = async (command: SearchCommand) =>
    (handlers[command.action] as (command: SearchCommand) => void | Promise<void>)(command);

  /** Commands that open each settings tab the user has access to. */
  const settingsCommands = computed<SearchCommand[]>(() =>
    [...settingsList.value.values()]
      .flat()
      .map(([tab, icon]) => ({action: 'open_settings', label: tab, icon, payload: {tab}})),
  );

  /** Commands to switch to the theme & language that are not active. */
  const preferenceCommands = computed<SearchCommand[]>(() => {
    const {theme, language} = userSettingsStore.userSettings;
    return [
      theme === 'dark-only'
        ? {
            action: 'switch_theme',
            label: 'Switch to light theme',
            icon: 'light-mode',
            payload: {theme: 'light-only'},
          }
        : {
            action: 'switch_theme',
            label: 'Switch to dark theme',
            icon: 'dark-mode',
            payload: {theme: 'dark-only'},
          },
      language === 'nl-NL'
        ? {
            action: 'switch_locale',
            label: 'Switch to English',
            payload: {language: 'en-US'},
          }
        : {
            action: 'switch_locale',
            label: 'Switch to Nederlands',
            payload: {language: 'nl-NL'},
          },
    ];
  });

  /** All commands that need no API to be offered. */
  const localCommands = computed<SearchCommand[]>(() => [
    ...settingsCommands.value,
    ...preferenceCommands.value,
    {action: 'logout', label: 'Log out', icon: 'logout'},
  ]);

  /** A short sentence describing what the command does, e.g. for hints. */
  const describe = (command: SearchCommand) =>
    command.action === 'open_settings'
      ? `${translate('Open settings')}: ${translate(command.label)}`
      : translate(command.label);

  return {execute, icons, headings, localCommands, describe};
}
